import { Text } from "@/elements/Text"
import { JobPostingPayRate, JobPostingPayType } from "@/gql/graphql"
import { ChevronDownIcon } from "@/icons/outline"
import { cn } from "@/utils/classnames"
import * as Popover from "@radix-ui/react-popover"
import clsx from "clsx"
import { useState } from "react"
import { ProposedRateForm } from "./ApplyToJobContent"

const formatText = (text: string) => {
  return text
    .replaceAll("_", " ")
    .toLowerCase()
    .split(" ")
    .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ")
}

type PayFrequencyDropdownProps = { className?: string } & ProposedRateForm

export const PayFrequencyDropdown = (proposedRate: PayFrequencyDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleSelect = (value: JobPostingPayRate | "FLAT_FEE") => {
    if (value === "FLAT_FEE") {
      proposedRate.setPayType(JobPostingPayType.FixedFee)
      proposedRate.setPayRateFrequency(undefined)
    } else {
      proposedRate.setPayType(JobPostingPayType.RateBased)
      proposedRate.setPayRateFrequency(value)
    }
    setIsOpen(false)
  }
  return (
    <div className={"relative mt-2 flex flex-col"}>
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <div
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "flex h-auto w-full cursor-pointer justify-start rounded-md border bg-transparent p-4 py-3 outline-none text-body1-md lg:text-body1-lg",
              proposedRate.error && "border-red-default",
              isOpen ? "border-primary-3" : "border-bg-3",
              proposedRate.className
            )}>
            <Text
              className={clsx(
                proposedRate.payType === JobPostingPayType.FixedFee || proposedRate.payRateFrequency
                  ? "text-fg-1"
                  : "text-fg-3"
              )}>
              {proposedRate.payType === JobPostingPayType.FixedFee
                ? "Flat fee"
                : proposedRate.payRateFrequency
                  ? `/ ${formatText(proposedRate.payRateFrequency)}`
                  : "Choose Frequency"}
            </Text>
            <ChevronDownIcon className="absolute right-4 top-1/2 size-6 -translate-y-1/2 stroke-fg-2" />
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={4}
            className="z-[102] my-2 w-[var(--radix-popover-trigger-width)] rounded-xl border border-bg-2 bg-bg-1 p-3 focus:outline-none focus-visible:outline-none"
            style={{
              boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.20)",
            }}
            tabIndex={-1}>
            <div className="flex w-full flex-col">
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payType === JobPostingPayType.FixedFee}
                value="FLAT_FEE"
              />
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payRateFrequency === JobPostingPayRate.Hour}
                value={JobPostingPayRate.Hour}
              />
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payRateFrequency === JobPostingPayRate.HalfDay}
                value={JobPostingPayRate.HalfDay}
              />
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payRateFrequency === JobPostingPayRate.Day}
                value={JobPostingPayRate.Day}
              />
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payRateFrequency === JobPostingPayRate.Week}
                value={JobPostingPayRate.Week}
              />
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payRateFrequency === JobPostingPayRate.Month}
                value={JobPostingPayRate.Month}
              />
              <DropdownItem
                handleSelect={handleSelect}
                selected={proposedRate.payRateFrequency === JobPostingPayRate.Year}
                value={JobPostingPayRate.Year}
              />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}

const DropdownItem = ({
  value,
  handleSelect,
  selected,
}: {
  value: JobPostingPayRate | "FLAT_FEE"
  handleSelect: (value: JobPostingPayRate | "FLAT_FEE") => void
  selected: boolean
}) => {
  return (
    <div
      className={clsx("flex cursor-pointer flex-row gap-x-2 rounded-md px-2 py-[10px] transition-all hover:bg-bg-2")}
      onClick={() => handleSelect(value)}>
      <div
        className={clsx(
          "relative flex size-5 items-center justify-center rounded-full border",
          selected ? "border-fg-1" : "border-bg-3"
        )}>
        {selected ? <div className="size-3 rounded-full bg-fg-1" /> : null}
      </div>
      <Text variant="body3">{formatText(value)}</Text>
    </div>
  )
}
